import { pauseAllMedia } from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import {
	isSliderComponent,
	type SliderComponent,
} from '@/scripts/theme/slider-component'
import { type StickyHeader } from '@/scripts/theme/sticky-header'
import { type DeferredMedia } from '@/scripts/theme/deferred-media'
import { type SlideChangedEvent } from '@/scripts/types/events'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { UcoastVideo } from '@/scripts/core/ucoast-video'

export class MediaGallery extends UcoastEl {
	static htmlSelector = 'media-gallery'
	elements: {
		liveRegion: HTMLElement
		viewer: HTMLElement | SliderComponent
		thumbnails?: SliderComponent
	}
	mql: MediaQueryList
	stickyHeader?: StickyHeader
	constructor() {
		super()
		this.elements = {
			liveRegion: q.rs('[id^="GalleryStatus"]', this),
			viewer: q.rs('[id^="GalleryViewer"]', this),
			thumbnails: q.os('[id^="GalleryThumbnails"]', this),
		}
		this.mql = window.matchMedia('(min-width: 750px)')

		this.elements.viewer.addEventListener(
			'slideChanged',
			this.onSlideChanged.bind(this)
		)
		this.elements.viewer.addEventListener(
			'click',
			this.onViewerNavigation.bind(this),
			{ capture: true }
		)

		if (!this.elements.thumbnails) return

		this.elements.thumbnails
			.querySelectorAll('[data-target]')
			.forEach((mediaToSwitch) => {
				if (!(mediaToSwitch instanceof HTMLElement)) return
				const button = q.rs('button', mediaToSwitch)
				button.addEventListener(
					'click',
					this.setActiveMedia.bind(
						this,
						mediaToSwitch.dataset.target,
						false
					)
				)
			})
		if (
			this.dataset.desktopLayout?.includes('thumbnail') &&
			this.mql.matches
		)
			this.removeListSemantic()
	}

	onViewerNavigation(event: MouseEvent) {
		if (!isSliderComponent(this.elements.viewer)) return

		const target = event.target
		if (!(target instanceof Element)) return
		const button = target.closest<HTMLButtonElement>(
			'.slider-button--prev, .slider-button--next'
		)
		if (!button || !this.elements.viewer.contains(button)) return

		const items = this.elements.viewer.sliderItemsToShow
		const currentPage = this.elements.viewer.currentPage
		if (!items?.length || !currentPage) return

		const direction = button.name === 'next' ? 1 : -1
		const nextIndex = Math.min(
			Math.max(currentPage - 1 + direction, 0),
			items.length - 1
		)
		const nextItem = items[nextIndex]
		if (nextItem) this.playActiveMedia(nextItem)
	}

	getActiveMediaParent(mediaId: string) {
		return q.rs(
			`[data-media-id="${mediaId}"]`,
			this.elements.viewer,
			'parentElement'
		)
	}

	getActiveThumbnail(mediaId: string) {
		if (!this.elements.thumbnails) return
		return q.os<HTMLElement>(
			`[data-target="${mediaId}"]`,
			this.elements.thumbnails
		)
	}

	onSlideChanged(event: SlideChangedEvent) {
		const activeItem = event.detail.currentElement
		this.elements.viewer
			.querySelectorAll('[data-media-id]')
			.forEach((element) => {
				element.classList.toggle('is-active', element === activeItem)
			})
		this.playActiveMedia(activeItem)

		if (!this.elements.thumbnails) return
		const mediaId = q.ra(
			activeItem,
			'data-media-id'
		)
		const thumbnail = this.getActiveThumbnail(mediaId)
		if (!thumbnail) return
		this.setActiveThumbnail(thumbnail)
	}

	setActiveMedia(mediaId: string, prepend: boolean) {
		const activeMedia = q.rs(
			`[data-media-id="${mediaId}"]`,
			this.elements.viewer
		)
		this.elements.viewer
			.querySelectorAll('[data-media-id]')
			.forEach((element) => {
				element.classList.remove('is-active')
			})
		activeMedia.classList.add('is-active')

		if (prepend) {
			const activeMediaParent = this.getActiveMediaParent(mediaId)
			activeMediaParent.prepend(activeMedia)
			if (this.elements.thumbnails) {
				const activeThumbnail = this.getActiveThumbnail(mediaId)
				if (activeThumbnail) activeMediaParent.prepend(activeThumbnail)
			}
			if (isSliderComponent(this.elements.viewer))
				this.elements.viewer.resetPages()
		}

		this.preventStickyHeader()
		window.setTimeout(() => {
			if (this.elements.thumbnails) {
				const activeMediaParent = this.getActiveMediaParent(mediaId)
				activeMediaParent.scrollTo({ left: activeMedia.offsetLeft })
			}
			if (
				!this.elements.thumbnails ||
				this.dataset.desktopLayout === 'stacked'
			) {
				activeMedia.scrollIntoView({ behavior: 'smooth' })
			}
		})
		this.playActiveMedia(activeMedia)

		if (!this.elements.thumbnails) return
		const activeThumbnail = this.getActiveThumbnail(mediaId)
		if (!activeThumbnail) return
		this.setActiveThumbnail(activeThumbnail)
		this.announceLiveRegion(
			activeMedia,
			q.ra(activeThumbnail, 'data-media-position')
		)
	}

	setActiveThumbnail(thumbnail?: HTMLElement) {
		if (!this.elements.thumbnails || !thumbnail) return

		this.elements.thumbnails
			.querySelectorAll('button')
			.forEach((element) => element.removeAttribute('aria-current'))
		const button = q.rs('button', thumbnail)
		button.setAttribute('aria-current', 'true')
		if (
			this.elements.thumbnails &&
			this.elements.thumbnails.isSlideVisible(thumbnail, 10)
		)
			return

		this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft })
	}

	announceLiveRegion(activeItem: HTMLElement, position: string) {
		const image = q.rs<HTMLImageElement>(
			'.product__modal-opener--image img',
			activeItem
		)
		if (!image) return
		image.onload = () => {
			this.elements.liveRegion.setAttribute('aria-hidden', 'false')
			this.elements.liveRegion.innerHTML =
				window.accessibilityStrings.imageAvailable.replace(
					'[index]',
					position
				)
			setTimeout(() => {
				this.elements.liveRegion.setAttribute('aria-hidden', 'true')
			}, 2000)
		}
		image.src = image.src
	}

	playActiveMedia(activeItem: HTMLElement) {
		pauseAllMedia()
		q.ol<UcoastVideo>('ucoast-video', this.elements.viewer)?.forEach(
			(video) => video.playEventOff()
		)

		const activeVideo = q.os<UcoastVideo>('ucoast-video', activeItem)
		if (activeVideo) {
			activeVideo.userPaused = false
			void activeVideo.playEventOn()
		}

		const deferredMedia = q.os<DeferredMedia>(
			'.deferred-media',
			activeItem
		)
		if (deferredMedia) deferredMedia.loadContent(false)
	}

	preventStickyHeader() {
		this.stickyHeader =
			this.stickyHeader || q.rs<StickyHeader>('sticky-header')
		if (!this.stickyHeader) return
		this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'))
	}

	removeListSemantic() {
		const slider = q.os<HTMLElement>('[id^="Slider-"]', this.elements.viewer)
		if (!slider) return
		slider.setAttribute('role', 'presentation')
		q.ol<HTMLElement>('[id^="Slide-"]', this.elements.viewer)?.forEach(
			(slide) => slide.setAttribute('role', 'presentation')
		)
	}
}
